import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Input, Select, Tag, Segmented } from 'antd';
import { PlusOutlined, SearchOutlined, HistoryOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from '../../config';
import CataloguePicker from '../journalTransactions/CataloguePicker';
import AcomptePanel from './AcomptePanel';
import usePermissions from '../../hooks/usePermissions';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeResponsiveTable, NdoeDataCard,
  NdoeStatusBadge, NdoeImage, NdoeLoading, NdoeError,
  NdoeArticleLink, useFicheArticle, optionsCouleursParFamille,
} from '../../components/ndoe';
import './reservations.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Badge d'état d'une réservation CLIENTE (échéance comprise).
const resaBadge = (r, alerteJours) => {
  if (r.statut === 'convertie') return <NdoeStatusBadge kind="custom" tone="gold" label="Convertie" />;
  if (r.statut === 'liberee') return <NdoeStatusBadge kind="custom" tone="neutral" label="Désactivée" />;
  if (r.statut === 'expiree' || Number(r.expiree_effective) === 1) return <NdoeStatusBadge kind="custom" tone="neutral" label="Expirée" />;
  if (r.expire_dans_jours !== null && Number(r.expire_dans_jours) <= alerteJours) {
    return <NdoeStatusBadge kind="custom" tone="warn" label={`Expire dans ${Math.max(0, Number(r.expire_dans_jours))} j`} />;
  }
  return <NdoeStatusBadge kind="custom" tone="ok" label="Active" />;
};
const echeanceTxt = (r) => {
  if (!r.expire_le) return 'Sans limite';
  if (Number(r.expiree_effective) === 1 || r.statut === 'expiree') return `Expirée le ${r.expire_le}`;
  return `${r.expire_le} (${Math.max(0, Number(r.expire_dans_jours))} j)`;
};
const paireTxt = (r) => `${r.nom_produit || ''}${r.taille ? ` · P${r.taille}` : ''}${r.couleur ? ` · ${r.couleur}` : ''}`;
const money = (v) => (v === null || v === undefined || v === '' ? null : `${Number(v).toLocaleString('fr-FR')} $`);

export default function Reservations() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const user = useSelector((s) => s.user?.currentUser);
  const allowed = ['admin', 'secretaire'].includes(String(user?.role || '').toLowerCase());
  const isAdmin = user?.role === 'admin';

  const [nature, setNature] = useState('toutes');
  const [statut, setStatut] = useState('active');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ clientes: [], commandes: [], alerte_jours: 3 });
  const [cfg, setCfg] = useState({ validiteJours: 30, validiteMaxJours: 90 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const { openFiche, ficheElement } = useFicheArticle();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${DOMAIN}/api/reservations`, { params: { nature, statut, q } });
      setData({ clientes: d.clientes || [], commandes: d.commandes || [], alerte_jours: d.alerte_jours || 3 });
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger les réservations.');
    } finally { setLoading(false); }
  }, [nature, statut, q]);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);
  useEffect(() => {
    if (!allowed) return;
    axios.get(`${DOMAIN}/api/reservations/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, [allowed]);

  // --- Création -----------------------------------------------------------
  const [createOpen, setCreateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [refLists, setRefLists] = useState({ categories: [], marques: [], couleurs: [] });
  const [form, setForm] = useState({ id_client: null, variante: null, qte: 1, validite_jours: '', prix_reserve: '', commentaire: '' });

  useEffect(() => {
    if (!createOpen || clients.length) return;
    axios.get(`${DOMAIN}/api/client`).then((r) => setClients(r.data || [])).catch(() => {});
    (async () => {
      try {
        const [c, m, l] = await Promise.all([
          axios.get(`${DOMAIN}/api/produit/categorie`),
          axios.get(`${DOMAIN}/api/produit/marque`),
          axios.get(`${DOMAIN}/api/produit/couleur`),
        ]);
        setRefLists({
          categories: (c.data || []).map((x) => ({ value: x.id_categorie, label: x.nom_categorie })),
          marques: (m.data || []).map((x) => ({ value: x.id_marque, label: (x.nom || '').trim() })),
          // Repliées par famille : 55 nuances tenaient dans une seule liste.
          couleurs: optionsCouleursParFamille(l.data || [], { avecNbNuances: true }),
        });
      } catch (e) { console.log(e); }
    })();
  }, [createOpen, clients.length]);

  const submitCreate = async () => {
    if (!form.id_client) return toast.error('Sélectionnez la cliente.');
    if (!form.variante) return toast.error('Choisissez la paire à réserver.');
    setBusy(true);
    try {
      await axios.post(`${DOMAIN}/api/reservations`, {
        id_varianteProduit: form.variante.id_varianteProduit,
        qte: Number(form.qte) || 1,
        id_client: form.id_client,
        validite_jours: form.validite_jours === '' ? undefined : Number(form.validite_jours),
        prix_reserve: form.prix_reserve === '' ? undefined : Number(form.prix_reserve),
        commentaire: form.commentaire,
      });
      toast.success('Réservation enregistrée.');
      setCreateOpen(false);
      setForm({ id_client: null, variante: null, qte: 1, validite_jours: '', prix_reserve: '', commentaire: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Création impossible.');
    } finally { setBusy(false); }
  };

  // --- Actions ------------------------------------------------------------
  const [deact, setDeact] = useState(null);       // { resa, motif }
  const [liberer, setLiberer] = useState(null);   // { resa, motif } — paire engagée (commande)
  const [prolong, setProlong] = useState(null);   // { resa, jours, motif }
  const [convert, setConvert] = useState(null);   // { resa, id_adresse, id_telephone, shop, adresses, telephones }
  const [history, setHistory] = useState(null);   // { resa, events }
  const [acompte, setAcompte] = useState(null);   // { type:'reservation', id } — cible du panneau acomptes

  const doDeactivate = async () => {
    if (!deact?.motif?.trim()) return toast.error('Le motif est obligatoire.');
    setBusy(true);
    try {
      await axios.put(`${DOMAIN}/api/reservations/${deact.resa.id_reservation}/desactiver`, { motif: deact.motif });
      toast.success('Réservation désactivée — la paire est de nouveau disponible.');
      setDeact(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Désactivation impossible.'); }
    finally { setBusy(false); }
  };

  // Libération FORCÉE (admin) d'une paire engagée par une commande — uniquement
  // quand elle est encore en rayon (AFFECTEE) ; sinon l'API renvoie vers le hub.
  const doLiberer = async () => {
    if (!liberer?.motif?.trim()) return toast.error('Le motif est obligatoire.');
    setBusy(true);
    try {
      await axios.put(`${DOMAIN}/api/reservations/commande/${liberer.resa.id_detail_livraison}/liberer`, { motif: liberer.motif });
      toast.success('Paire libérée — elle redevient disponible immédiatement.');
      setLiberer(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Libération impossible.'); }
    finally { setBusy(false); }
  };

  const doProlong = async () => {
    setBusy(true);
    try {
      await axios.put(`${DOMAIN}/api/reservations/${prolong.resa.id_reservation}/prolonger`, {
        jours: Number(prolong.jours) || 0, motif: prolong.motif,
      });
      toast.success('Réservation prolongée.');
      setProlong(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Prolongation impossible.'); }
    finally { setBusy(false); }
  };

  const doReactivate = async (r) => {
    setBusy(true);
    try {
      await axios.put(`${DOMAIN}/api/reservations/${r.id_reservation}/reactiver`, {});
      toast.success('Réservation réactivée.');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Réactivation impossible.'); }
    finally { setBusy(false); }
  };

  const openConvert = async (r) => {
    setConvert({ resa: r, id_adresse: null, id_telephone: null, shop: '', adresses: [], telephones: [] });
    try {
      const [a, t] = await Promise.all([
        axios.get(`${DOMAIN}/api/client/clientAdresse/${r.id_client}`),
        axios.get(`${DOMAIN}/api/client/clientTelephone/${r.id_client}`),
      ]);
      setConvert((prev) => prev && { ...prev, adresses: a.data || [], telephones: t.data || [] });
    } catch (e) { console.log(e); }
  };

  const doConvert = async () => {
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${DOMAIN}/api/reservations/${convert.resa.id_reservation}/convertir`, {
        id_adresse: convert.id_adresse, id_telephone: convert.id_telephone, shop: convert.shop,
      });
      toast.success(`Commande #${d.result.id_commande} créée (prix appliqué : ${d.result.prix_applique} $).`);
      setConvert(null);
      navigate(`/listeDetailView/${d.result.id_commande}`);
    } catch (e) { toast.error(e.response?.data?.error || 'Conversion impossible.'); }
    finally { setBusy(false); }
  };

  const openHistory = async (r) => {
    setHistory({ resa: r, events: null });
    try {
      const { data: d } = await axios.get(`${DOMAIN}/api/reservations/${r.id_reservation}/historique`);
      setHistory((prev) => prev && { ...prev, events: d.evenements || [] });
    } catch (e) { setHistory((prev) => prev && { ...prev, events: [] }); }
  };

  // --- Rendus -------------------------------------------------------------
  const actionsFor = (r) => {
    const list = [];
    const activeNonExpiree = r.statut === 'active' && Number(r.expiree_effective) !== 1;
    const expiree = r.statut === 'expiree' || (r.statut === 'active' && Number(r.expiree_effective) === 1);
    if (activeNonExpiree) {
      list.push(<NdoeActionButton key="c" size="sm" variant="primary" onClick={() => openConvert(r)}>Convertir</NdoeActionButton>);
      // Acompte : visible pour qui peut encaisser (les livreurs ne gèrent pas les
      // réservations, donc pas de garde de layout à prévoir ici).
      if (can('reservations.acompte')) {
        list.push(<NdoeActionButton key="a" size="sm" variant="secondary" onClick={() => setAcompte({ type: 'reservation', id: r.id_reservation })}>Acompte</NdoeActionButton>);
      }
      list.push(<NdoeActionButton key="p" size="sm" variant="secondary" onClick={() => setProlong({ resa: r, jours: 7, motif: '' })}>Prolonger</NdoeActionButton>);
      list.push(<NdoeActionButton key="d" size="sm" variant="danger" onClick={() => setDeact({ resa: r, motif: '' })}>Désactiver</NdoeActionButton>);
    }
    if (expiree) list.push(<NdoeActionButton key="r" size="sm" variant="ink" onClick={() => doReactivate(r)} loading={busy}>Réactiver</NdoeActionButton>);
    if (r.statut === 'convertie' && r.id_commande) {
      list.push(<NdoeActionButton key="v" size="sm" variant="secondary" onClick={() => navigate(`/listeDetailView/${r.id_commande}`)}>Voir la commande</NdoeActionButton>);
    }
    list.push(<NdoeActionButton key="h" size="sm" variant="ghost" icon={<HistoryOutlined />} aria-label="Historique" iconOnly onClick={() => openHistory(r)} />);
    return list;
  };

  const clienteCols = [
    { title: 'Photo', dataIndex: 'img', key: 'img', render: (t, r) => <NdoeImage src={r.img} alt={paireTxt(r)} size="lg" /> },
    { title: 'Paire', key: 'paire', render: (t, r) => <NdoeArticleLink onClick={() => openFiche(r.id_produit, r.id_varianteProduit)}><b>{paireTxt(r)}</b></NdoeArticleLink> },
    { title: 'Cliente', dataIndex: 'cliente', key: 'cliente', render: (t) => t || '—' },
    { title: 'Qté', dataIndex: 'qte', key: 'qte' },
    { title: 'Réservée le', dataIndex: 'reservee_le', key: 'reservee_le' },
    { title: 'Échéance', key: 'echeance', render: (t, r) => <span className="resa-echeance">{echeanceTxt(r)}</span> },
    { title: 'Prix promis', key: 'prix', render: (t, r) => money(r.prix_reserve) || <span style={{ color: 'var(--ndoe-muted)' }}>prix courant</span> },
    { title: 'Statut', key: 'statut', render: (t, r) => resaBadge(r, data.alerte_jours) },
    { title: 'Actions', key: 'actions', render: (t, r) => <div className="resa-actions">{actionsFor(r)}</div> },
  ];
  const renderClienteCard = (r) => (
    <NdoeDataCard
      media={<NdoeImage src={r.img} alt={paireTxt(r)} size="lg" />}
      title={<NdoeArticleLink onClick={() => openFiche(r.id_produit, r.id_varianteProduit)}>{paireTxt(r)}</NdoeArticleLink>}
      subtitle={`${r.cliente || '—'} · réservée le ${r.reservee_le}`}
      badge={resaBadge(r, data.alerte_jours)}
      metrics={[
        { value: r.qte, label: 'qté' },
        { value: echeanceTxt(r), label: 'échéance' },
        ...(money(r.prix_reserve) ? [{ value: money(r.prix_reserve), label: 'prix promis' }] : []),
      ]}
      actions={actionsFor(r)}
    />
  );

  const commandeCols = [
    { title: 'Photo', dataIndex: 'img', key: 'img', render: (t, r) => <NdoeImage src={r.img} alt={paireTxt(r)} size="lg" /> },
    { title: 'Paire', key: 'paire', render: (t, r) => <NdoeArticleLink onClick={() => openFiche(r.id_produit, r.id_varianteProduit)}><b>{paireTxt(r)}</b></NdoeArticleLink> },
    { title: 'Commande', key: 'cmd', render: (t, r) => <>#{r.id_commande} · {r.cliente}</> },
    { title: 'Livreur', dataIndex: 'livreur', key: 'livreur' },
    { title: 'Qté', dataIndex: 'qte', key: 'qte' },
    { title: 'Réservée le', dataIndex: 'reservee_le', key: 'reservee_le' },
    { title: 'Statut', key: 'statut', render: (t, r) => <NdoeStatusBadge status={r.statut} /> },
    {
      title: 'Actions', key: 'a',
      render: (t, r) => (
        <div className="resa-actions">
          <NdoeActionButton size="sm" variant="secondary" onClick={() => navigate(`/listeDetailView/${r.id_commande}`)}>Voir la livraison</NdoeActionButton>
          {isAdmin && (r.statut || 'AFFECTEE') === 'AFFECTEE' && (
            <NdoeActionButton size="sm" variant="danger" onClick={() => setLiberer({ resa: r, motif: '' })}>Libérer</NdoeActionButton>
          )}
        </div>
      ),
    },
  ];
  const renderCommandeCard = (r) => (
    <NdoeDataCard
      media={<NdoeImage src={r.img} alt={paireTxt(r)} size="lg" />}
      title={<NdoeArticleLink onClick={() => openFiche(r.id_produit, r.id_varianteProduit)}>{paireTxt(r)}</NdoeArticleLink>}
      subtitle={`Commande #${r.id_commande} · ${r.cliente} · ${r.livreur}`}
      badge={<NdoeStatusBadge status={r.statut} />}
      metrics={[{ value: r.qte, label: 'qté' }, { value: r.reservee_le, label: 'réservée le' }]}
      actions={[
        <NdoeActionButton key="v" size="sm" variant="secondary" onClick={() => navigate(`/listeDetailView/${r.id_commande}`)}>Voir la livraison</NdoeActionButton>,
        ...(isAdmin && (r.statut || 'AFFECTEE') === 'AFFECTEE'
          ? [<NdoeActionButton key="l" size="sm" variant="danger" onClick={() => setLiberer({ resa: r, motif: '' })}>Libérer</NdoeActionButton>]
          : []),
      ]}
    />
  );

  const bientotCount = useMemo(
    () => data.clientes.filter((r) => r.statut === 'active' && r.expire_dans_jours !== null && Number(r.expire_dans_jours) <= data.alerte_jours && Number(r.expiree_effective) !== 1).length,
    [data],
  );

  if (!allowed) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Commerce" title="Réservations" />
        <NdoeError title="Accès réservé" text="Ce module est réservé à l'administration et au secrétariat." />
      </NdoePage>
    );
  }

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Commerce"
        title="Réservations"
        subtitle="Paires gardées pour une cliente ou engagées dans une commande — validité, conversion, libération."
        actions={<NdoeActionButton variant="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>Réserver une paire</NdoeActionButton>}
      />

      <div className="resa-toolbar">
        <Segmented value={nature} onChange={setNature} options={[
          { value: 'toutes', label: 'Toutes' }, { value: 'cliente', label: 'Clientes' }, { value: 'commande', label: 'Commandes' },
        ]} />
        <Segmented value={statut} onChange={setStatut} options={[
          { value: 'active', label: 'Actives' },
          { value: 'bientot', label: `Expirent bientôt${bientotCount ? ` (${bientotCount})` : ''}` },
          { value: 'historique', label: 'Historique' },
        ]} />
        <Input allowClear className="resa-search" prefix={<SearchOutlined />} placeholder="Cliente, produit, référence…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {error ? <NdoeError title="Erreur" text={error} onRetry={load} /> : loading ? <NdoeLoading rows={4} /> : (
        <>
          {nature !== 'commande' && (
            <div className="ndoe-card resa-block">
              <div className="ndoe-section__title">Réservations clientes {statut === 'historique' ? '· historique' : ''}</div>
              <NdoeResponsiveTable
                data={data.clientes}
                columns={clienteCols}
                rowKey="id_reservation"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
                renderCard={renderClienteCard}
                empty={{ icon: '👜', title: 'Aucune réservation cliente', text: 'Réservez une paire avec le bouton en haut de page.' }}
              />
            </div>
          )}
          {nature !== 'cliente' && statut !== 'historique' && (
            <div className="ndoe-card resa-block">
              <div className="ndoe-section__title">Paires engagées dans une commande (livraison)</div>
              <NdoeResponsiveTable
                data={data.commandes}
                columns={commandeCols}
                rowKey="id_detail_livraison"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
                renderCard={renderCommandeCard}
                empty={{ icon: '🚚', title: 'Aucune paire engagée', text: 'Les paires affectées à un livreur apparaîtront ici.' }}
              />
            </div>
          )}
        </>
      )}

      {/* --- Création --- */}
      <Modal open={createOpen} onCancel={() => setCreateOpen(false)} footer={null} width={620} title="Réserver une paire" centered>
        <div className="ndoe-form-grid">
          <div className="form-controle ndoe-form-grid--full">
            <label>Cliente <span style={{ color: 'red' }}>*</span></label>
            <Select
              showSearch optionFilterProp="label" placeholder="Sélectionnez la cliente" style={{ width: '100%' }}
              value={form.id_client} onChange={(v) => setForm((f) => ({ ...f, id_client: v }))}
              options={clients.map((c) => ({ value: c.id, label: c.nom }))}
            />
          </div>
          <div className="form-controle ndoe-form-grid--full">
            <label>Paire <span style={{ color: 'red' }}>*</span></label>
            {form.variante ? (
              <div className="resa-variante-pick">
                <NdoeImage src={form.variante.img} alt={form.variante.nom_produit || 'Paire'} size="md" />
                <div className="resa-variante-txt">
                  <b>{form.variante.nom_produit}</b>
                  <small>{form.variante.couleur || '—'} · P{form.variante.taille ?? '?'} · {form.variante.disponible} dispo</small>
                </div>
                <NdoeActionButton size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>Changer</NdoeActionButton>
              </div>
            ) : (
              <NdoeActionButton variant="ink" onClick={() => setPickerOpen(true)}>Choisir dans le catalogue…</NdoeActionButton>
            )}
          </div>
          <div className="form-controle">
            <label>Quantité</label>
            <Input type="number" min={1} value={form.qte} onChange={(e) => setForm((f) => ({ ...f, qte: e.target.value }))} />
          </div>
          <div className="form-controle">
            <label>Validité (jours)</label>
            <Input type="number" min={1} placeholder={`${cfg.validiteJours} (défaut)`} value={form.validite_jours}
              onChange={(e) => setForm((f) => ({ ...f, validite_jours: e.target.value }))} />
            <small style={{ color: 'var(--ndoe-muted)' }}>Max {cfg.validiteMaxJours} j{isAdmin ? ' (admin : sans plafond)' : ''}</small>
          </div>
          <div className="form-controle">
            <label>Prix promis ($)</label>
            <Input type="number" min={0} placeholder="Prix courant si vide" value={form.prix_reserve}
              onChange={(e) => setForm((f) => ({ ...f, prix_reserve: e.target.value }))} />
          </div>
          <div className="form-controle ndoe-form-grid--full">
            <label>Commentaire</label>
            <Input.TextArea rows={2} maxLength={255} value={form.commentaire} onChange={(e) => setForm((f) => ({ ...f, commentaire: e.target.value }))} />
          </div>
        </div>
        <div className="resa-modal-actions">
          <NdoeActionButton variant="secondary" onClick={() => setCreateOpen(false)}>Annuler</NdoeActionButton>
          <NdoeActionButton variant="primary" onClick={submitCreate} loading={busy}>Réserver</NdoeActionButton>
        </div>
      </Modal>
      <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)} refLists={refLists}
        onSelect={(v) => { setForm((f) => ({ ...f, variante: v })); setPickerOpen(false); }} />
      {ficheElement}

      {/* --- Désactivation --- */}
      <Modal open={!!deact} onCancel={() => setDeact(null)} footer={null} width={460} title="Désactiver la réservation" centered>
        {deact && (
          <>
            <p style={{ color: 'var(--ndoe-muted)' }}>{paireTxt(deact.resa)} — {deact.resa.cliente}. La paire redevient disponible immédiatement.</p>
            <div className="form-controle">
              <label>Motif <span style={{ color: 'red' }}>*</span></label>
              <Input.TextArea rows={2} maxLength={200} value={deact.motif} onChange={(e) => setDeact((d) => ({ ...d, motif: e.target.value }))}
                placeholder="Ex. la cliente a renoncé / achetée en boutique" />
            </div>
            <div className="resa-modal-actions">
              <NdoeActionButton variant="secondary" onClick={() => setDeact(null)}>Annuler</NdoeActionButton>
              <NdoeActionButton variant="danger" onClick={doDeactivate} loading={busy}>Désactiver</NdoeActionButton>
            </div>
          </>
        )}
      </Modal>

      {/* --- Libération forcée d'une paire engagée (admin) --- */}
      <Modal open={!!liberer} onCancel={() => setLiberer(null)} footer={null} width={480} title="Libérer la paire engagée" centered>
        {liberer && (
          <>
            <p style={{ color: 'var(--ndoe-muted)' }}>
              {paireTxt(liberer.resa)} — commande #{liberer.resa.id_commande} ({liberer.resa.cliente}).
              La paire est encore en rayon : elle sera détachée de cette commande et redeviendra
              disponible immédiatement. La livraison, elle, reste à traiter côté commande.
            </p>
            <div className="form-controle">
              <label>Motif <span style={{ color: 'red' }}>*</span></label>
              <Input.TextArea rows={2} maxLength={200} value={liberer.motif} onChange={(e) => setLiberer((d) => ({ ...d, motif: e.target.value }))}
                placeholder="Ex. commande abandonnée / paire réattribuée à une vente boutique" />
            </div>
            <div className="resa-modal-actions">
              <NdoeActionButton variant="secondary" onClick={() => setLiberer(null)}>Annuler</NdoeActionButton>
              <NdoeActionButton variant="danger" onClick={doLiberer} loading={busy}>Libérer</NdoeActionButton>
            </div>
          </>
        )}
      </Modal>

      {/* --- Prolongation --- */}
      <Modal open={!!prolong} onCancel={() => setProlong(null)} footer={null} width={460} title="Prolonger la réservation" centered>
        {prolong && (
          <>
            <p style={{ color: 'var(--ndoe-muted)' }}>{paireTxt(prolong.resa)} — échéance actuelle : {prolong.resa.expire_le || 'sans limite'}.</p>
            <div className="ndoe-form-grid">
              <div className="form-controle">
                <label>Jours à ajouter</label>
                <Input type="number" min={1} value={prolong.jours} onChange={(e) => setProlong((p) => ({ ...p, jours: e.target.value }))} />
              </div>
              <div className="form-controle">
                <label>Motif</label>
                <Input maxLength={200} value={prolong.motif} onChange={(e) => setProlong((p) => ({ ...p, motif: e.target.value }))} placeholder="Ex. cliente en voyage" />
              </div>
            </div>
            <div className="resa-modal-actions">
              <NdoeActionButton variant="secondary" onClick={() => setProlong(null)}>Annuler</NdoeActionButton>
              <NdoeActionButton variant="primary" onClick={doProlong} loading={busy}>Prolonger</NdoeActionButton>
            </div>
          </>
        )}
      </Modal>

      {/* --- Conversion en commande --- */}
      <Modal open={!!convert} onCancel={() => setConvert(null)} footer={null} width={560} title="Créer la commande à partir de la réservation" centered>
        {convert && (
          <>
            <p style={{ color: 'var(--ndoe-muted)' }}>
              {paireTxt(convert.resa)} × {convert.resa.qte} — {convert.resa.cliente}.
              Prix appliqué : <b>{money(convert.resa.prix_reserve) || `${money(convert.resa.prix_courant) || '—'} (prix courant)`}</b>.
            </p>
            <div className="ndoe-form-grid">
              <div className="form-controle">
                <label>Adresse de livraison</label>
                <Select allowClear style={{ width: '100%' }} placeholder="Optionnel" value={convert.id_adresse}
                  onChange={(v) => setConvert((c) => ({ ...c, id_adresse: v }))}
                  options={(convert.adresses || []).map((a) => ({ value: a.id_adresse, label: `${a.nom_province} · C/${a.nom_commune} Av/${a.avenue || '—'}` }))} />
              </div>
              <div className="form-controle">
                <label>Téléphone</label>
                <Select allowClear style={{ width: '100%' }} placeholder="Optionnel" value={convert.id_telephone}
                  onChange={(v) => setConvert((c) => ({ ...c, id_telephone: v }))}
                  options={(convert.telephones || []).map((t) => ({ value: t.id_telephone, label: t.numero }))} />
              </div>
              <div className="form-controle">
                <label>Shop</label>
                <Input value={convert.shop} onChange={(e) => setConvert((c) => ({ ...c, shop: e.target.value }))} placeholder="Ex. Ngaliema" />
              </div>
            </div>
            <div className="resa-modal-actions">
              <NdoeActionButton variant="secondary" onClick={() => setConvert(null)}>Annuler</NdoeActionButton>
              <NdoeActionButton variant="primary" onClick={doConvert} loading={busy}>Créer la commande</NdoeActionButton>
            </div>
          </>
        )}
      </Modal>

      {/* --- Historique --- */}
      <Modal open={!!history} onCancel={() => setHistory(null)} footer={null} width={560} title="Historique de la réservation" centered>
        {history && (history.events === null ? <NdoeLoading rows={2} /> : (
          history.events.length === 0 ? <p style={{ color: 'var(--ndoe-muted)' }}>Aucun évènement.</p> : (
            <div className="resa-history">
              {history.events.map((e, i) => (
                <div className="resa-history-row" key={i}>
                  <Tag color="gold">{e.action}</Tag>
                  <div>
                    <div style={{ fontSize: 13 }}>{e.motif || '—'}</div>
                    <small style={{ color: 'var(--ndoe-muted)' }}>{e.quand_} · {e.acteur || 'système'}</small>
                  </div>
                </div>
              ))}
            </div>
          )
        ))}
      </Modal>

      <AcomptePanel
        cible={acompte}
        open={!!acompte}
        onClose={() => setAcompte(null)}
        onChange={load}
      />
    </NdoePage>
  );
}
